import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ShieldWarning, ChatCircle, User, Phone, ArrowUpRight, CheckCircle,
  Prohibit, Clock, Warning,
} from "@phosphor-icons/react";

const REASON_STYLE = {
  sensitive_data: "bg-[#F4C7B9] text-[#7A2E18]",
  hack_attempt: "bg-[#7A2E18] text-white",
  malware_payload: "bg-[#7A2E18] text-white",
  spam: "bg-[#F9E4B7] text-[#7A5A11]",
  custom_blocklist: "bg-[#0B2B66] text-white",
};

export default function HandoffQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // conversation id being acted on

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/crm/handoff-queue");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reactivate = async (cid) => {
    if (!confirm("Reactivate this customer and clear the safety flag?")) return;
    setBusy(cid);
    try {
      await api.post(`/crm/handoff-queue/${cid}/reactivate`);
      toast.success("Customer reactivated, flag cleared");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const dismiss = async (cid) => {
    if (!confirm("Dismiss this flag WITHOUT reactivating the customer?")) return;
    setBusy(cid);
    try {
      await api.post(`/crm/handoff-queue/${cid}/dismiss`);
      toast.success("Flag dismissed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const fmt = (v) => {
    if (!v) return "—";
    try { return new Date(v).toLocaleString("en-IN"); } catch { return String(v); }
  };
  const highlight = (text, needle) => {
    if (!text) return "—";
    if (!needle) return text;
    try {
      const idx = text.toLowerCase().indexOf(needle.toLowerCase());
      if (idx < 0) return text;
      return (
        <>
          {text.slice(0, idx)}
          <span className="bg-[#F4C7B9] text-[#7A2E18] px-1 rounded font-semibold">
            {text.slice(idx, idx + needle.length)}
          </span>
          {text.slice(idx + needle.length)}
        </>
      );
    } catch { return text; }
  };

  return (
    <AppShell
      title="AI Handoff Queue"
      subtitle={`Safety-flagged chats · ${rows.length} awaiting senior triage`}
    >
      {loading && <div className="text-sm text-[#8A8780] mt-4">Loading queue…</div>}
      {!loading && rows.length === 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-md p-8 text-center mt-4"
             data-testid="handoff-empty">
          <ShieldWarning size={32} className="mx-auto text-[#8A8780]"/>
          <div className="font-heading text-lg mt-2">All clear</div>
          <div className="text-sm text-[#5C5A55] mt-1">
            No chats are currently flagged for senior handoff.
          </div>
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="space-y-2 mt-4" data-testid="handoff-list">
          {rows.map(row => {
            const reason = row.safety_reason || "unknown";
            const reasonClass = REASON_STYLE[reason] || "bg-[#8A8780] text-white";
            return (
              <div key={row.conversation_id}
                    data-testid={`handoff-row-${row.conversation_id}`}
                    className="bg-white border border-[#C25D38] rounded-md p-4 flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span data-testid={`handoff-reason-${row.conversation_id}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold ${reasonClass}`}>
                      <ShieldWarning size={11}/>{reason.replace(/_/g, " ")}
                    </span>
                    <span className="text-[10px] text-[#8A8780] inline-flex items-center gap-0.5">
                      <Clock size={10}/>{fmt(row.safety_flagged_at)}
                    </span>
                    {row.customer_status === "inactive" && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#EEEBE2] text-[#5C5A55]">
                        <Prohibit size={10}/>Customer inactive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <User size={14} className="text-[#2D5A27]"/>
                    <span className="font-heading text-base font-medium">
                      {row.customer_name || "Guest"}
                    </span>
                    {row.customer_code && (
                      <span className="text-[10px] font-mono text-[#8A8780]">
                        · {row.customer_code}
                      </span>
                    )}
                    {row.customer_phone && (
                      <span className="text-[11px] font-mono text-[#5C5A55] inline-flex items-center gap-1">
                        <Phone size={10}/>{row.customer_phone}
                      </span>
                    )}
                  </div>
                  {row.offending_message?.text && (
                    <div className="mt-2 bg-[#F7F6F2] border border-[#E5E2D9] rounded p-2.5 text-sm"
                         data-testid={`handoff-msg-${row.conversation_id}`}>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#8A8780] mb-0.5">
                        Offending message · {fmt(row.offending_message.created_at)}
                      </div>
                      <div className="text-[#3A3A38] leading-relaxed">
                        {highlight(row.offending_message.text, row.safety_matched)}
                      </div>
                    </div>
                  )}
                  {row.safety_matched && !row.offending_message?.text && (
                    <div className="mt-1 text-[11px] text-[#5C5A55]">
                      Match: <span className="font-mono text-[#7A2E18]">{row.safety_matched}</span>
                    </div>
                  )}
                </div>
                <div className="flex sm:flex-col gap-2 shrink-0">
                  <Link to={`/crm/chats?open=${row.conversation_id}`}
                        data-testid={`handoff-open-${row.conversation_id}`}
                        className="h-9 px-3 border border-[#0B2B66] text-[#0B2B66] rounded text-xs font-medium inline-flex items-center gap-1 hover:bg-[#E4EAF5]">
                    <ChatCircle size={13}/>Open chat<ArrowUpRight size={11} weight="bold"/>
                  </Link>
                  <button onClick={() => reactivate(row.conversation_id)}
                          disabled={busy === row.conversation_id}
                          data-testid={`handoff-reactivate-${row.conversation_id}`}
                          className="h-9 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-xs font-medium inline-flex items-center gap-1 disabled:opacity-60">
                    <CheckCircle size={13} weight="fill"/>Reactivate
                  </button>
                  <button onClick={() => dismiss(row.conversation_id)}
                          disabled={busy === row.conversation_id}
                          data-testid={`handoff-dismiss-${row.conversation_id}`}
                          className="h-9 px-3 border border-[#8A8780] text-[#5C5A55] rounded text-xs font-medium inline-flex items-center gap-1 disabled:opacity-60 hover:bg-[#F7F6F2]">
                    <Warning size={13}/>Dismiss
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
